import { RefreshCw } from 'lucide-react'

// Hardening Sprint Track 5.3 — reusable loading / empty / error state
// wrapper.
//
// Standardizes the three "no data on screen" cases every data-fetching
// surface eventually has to render:
//   - loading: initial fetch in flight
//   - error:   fetch landed but failed (RLS denial, network, etc)
//   - empty:   fetch succeeded with zero rows
//
// Default styling reads as muted text with a small icon. Components
// that need a different palette (Recon green vs Cipher amber) pass
// `tone` to switch the color set, or override individual messages.
//
// Usage:
//
//   <DataState
//     loading={loading}
//     error={fetchError}
//     empty={items.length === 0}
//     emptyMessage="No bounties active"
//     emptyHint="A GM will dispatch them when the event goes live."
//     onRetry={refetch}
//   >
//     {items.map(...)}
//   </DataState>
//
// onRetry is optional; when present the error state surfaces a retry
// button. Without it the error stays inert (caller might handle retry
// from a parent reload).

const TONES = {
  recon:   { muted: '#5a6a60', accent: '#9adb6e', danger: '#ff6a3d' },
  cipher:  { muted: '#2a6a3a', accent: '#9aff9a', danger: '#ff2a2a' },
  dossier: { muted: '#7a7058', accent: '#3a6a3a', danger: '#a82a1f' },
  command: { muted: '#5a6470', accent: '#4dd6c2', danger: '#ff6a3d' },
}

export default function DataState({
  loading,
  error,
  empty,
  tone = 'recon',
  loadingMessage = 'Loading…',
  emptyMessage = 'No data',
  emptyHint = null,
  errorMessage = null,
  onRetry = null,
  children,
}) {
  const palette = TONES[tone] || TONES.recon
  const baseFont = { fontFamily: "'JetBrains Mono', monospace" }
  const wrap = { textAlign: 'center', padding: '32px 16px' }

  if (loading) {
    return (
      <div style={wrap}>
        <p style={{ ...baseFont, fontSize: 12, color: palette.muted, letterSpacing: '0.1em', textTransform: 'uppercase' }}>
          {loadingMessage}
        </p>
      </div>
    )
  }

  if (error) {
    const msg = errorMessage || (typeof error === 'string' ? error : (error?.friendlyMessage || error?.message || 'Failed to load.'))
    return (
      <div style={wrap}>
        <p style={{ ...baseFont, fontSize: 13, color: palette.danger, marginBottom: 8 }}>
          {msg}
        </p>
        {onRetry && (
          <button onClick={onRetry} style={{
            ...baseFont, fontSize: 11, letterSpacing: '0.08em', textTransform: 'uppercase',
            padding: '8px 16px', minHeight: 44, minWidth: 44,
            background: 'rgba(0,0,0,.3)', color: palette.accent,
            border: `1px solid ${palette.accent}66`, cursor: 'pointer',
            display: 'inline-flex', alignItems: 'center', gap: 6,
          }}>
            <RefreshCw size={12} /> Retry
          </button>
        )}
      </div>
    )
  }

  if (empty) {
    return (
      <div style={wrap}>
        <p style={{ ...baseFont, fontSize: 13, color: palette.muted, letterSpacing: '0.08em', textTransform: 'uppercase' }}>
          {emptyMessage}
        </p>
        {emptyHint && (
          <p style={{ ...baseFont, fontSize: 11, color: palette.muted, opacity: 0.7, marginTop: 6 }}>
            {emptyHint}
          </p>
        )}
      </div>
    )
  }

  return children
}
